import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Invite, type InviteCreated } from "../../api/client";
import { readCsrfCookie } from "../auth/csrfCookie";

export interface UseInvitesResult {
  invites: Invite[];
  loading: boolean;
  error: string | null;
  /** The most recently created invite's plaintext token — shown exactly
   * once, since the API never re-exposes it (no email integration yet). */
  lastCreated: InviteCreated | null;
  create: (username: string, role: string) => Promise<boolean>;
  revoke: (inviteId: string) => Promise<boolean>;
  dismissLastCreated: () => void;
}

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "something went wrong";
}

/** Manages the tenant's pending invites: list, issue, revoke. */
export function useInvites(): UseInvitesResult {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<InviteCreated | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { invites } = await api.listInvites();
      setInvites(invites);
      setError(null);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (username: string, role: string): Promise<boolean> => {
      const csrfToken = readCsrfCookie(document.cookie);
      try {
        const created = await api.createInvite(csrfToken, username, role);
        setLastCreated(created);
        await refresh();
        return true;
      } catch (err) {
        setError(describeError(err));
        return false;
      }
    },
    [refresh],
  );

  const revoke = useCallback(
    async (inviteId: string): Promise<boolean> => {
      const csrfToken = readCsrfCookie(document.cookie);
      try {
        await api.revokeInvite(csrfToken, inviteId);
        await refresh();
        return true;
      } catch (err) {
        setError(describeError(err));
        return false;
      }
    },
    [refresh],
  );

  return { invites, loading, error, lastCreated, create, revoke, dismissLastCreated: () => setLastCreated(null) };
}
